import type { SxProps } from "@mui/material";
import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import type { AdaptiveTextConfig, ThemeV2 } from "../../../util/types";
import {
  defaultAdaptiveTextGroup,
  useAdaptiveTextScope,
} from "./AdaptiveTextScope";

type Typography = ThemeV2["tokens"]["typography"];
type TypographySize = keyof Typography["size"];

const orderedSizes: readonly TypographySize[] = ["xl", "lg", "md", "sm", "xs"];

export const availableFontSizes = (
  sizes: Typography["size"],
  normalSize: TypographySize,
  minimumSize: TypographySize,
) => {
  const start = orderedSizes.indexOf(normalSize);
  const end = orderedSizes.indexOf(minimumSize);
  const keys = orderedSizes.slice(
    Math.min(start, end),
    Math.max(start, end) + 1,
  );

  return [...new Set(keys.map((key) => sizes[key]))].sort(
    (left, right) => right - left,
  );
};

const renderedLineHeight = (element: HTMLElement, fontSize: number) => {
  const value = Number.parseFloat(getComputedStyle(element).lineHeight);
  if (!Number.isFinite(value)) return fontSize * 1.2;
  return value <= 3 ? value * fontSize : value;
};

type AdaptiveTextFitOptions = {
  adaptiveText?: AdaptiveTextConfig;
  contentKey: string;
  typography: Typography;
};

export const useAdaptiveTextFit = ({
  adaptiveText,
  contentKey,
  typography,
}: AdaptiveTextFitOptions) => {
  const enabled = adaptiveText !== undefined;
  const maxLines = adaptiveText?.maxLines ?? 3;
  const normalSize = adaptiveText?.normalSize;
  const minimumSize = adaptiveText?.minimumSize ?? "xs";
  const group = adaptiveText?.group ?? defaultAdaptiveTextGroup;
  const memberId = useId();
  const scope = useAdaptiveTextScope();
  const reportCandidate = scope?.reportCandidate;
  const unregister = scope?.unregister;
  const sharedFontSize = scope?.fontSizes.get(group);
  const sharedFontSizeRef = useRef(sharedFontSize);
  const elementRef = useRef<HTMLDivElement>(null);
  const measuredWidth = useRef(0);
  const [candidateFontSize, setCandidateFontSize] = useState(
    typography.size[normalSize ?? "md"],
  );
  const [breakLongWords, setBreakLongWords] = useState(false);
  const [ready, setReady] = useState(!enabled);
  const fontSize = Math.min(
    candidateFontSize,
    sharedFontSize ?? candidateFontSize,
  );

  const applyFontSize = useCallback((element: HTMLElement, size: number) => {
    element.style.setProperty("--adaptive-text-font-size", `${size}px`);
    element.style.fontSize = "var(--adaptive-text-font-size)";
  }, []);

  const fit = useCallback(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;

    element.style.removeProperty("font-size");
    const renderedFontSize = Number.parseFloat(
      getComputedStyle(element).fontSize,
    );
    const activeNormalSize =
      normalSize ??
      orderedSizes.find(
        (size) => typography.size[size] <= renderedFontSize + 0.5,
      ) ??
      minimumSize;
    const candidates = availableFontSizes(
      typography.size,
      activeNormalSize,
      minimumSize,
    );
    const width = element.getBoundingClientRect().width;
    measuredWidth.current = width;
    let selected = candidates.at(-1)!;
    let fitsTarget = false;
    let shouldBreakLongWords = false;

    element.style.overflowWrap = "normal";

    if (width > 0) {
      for (const candidate of candidates) {
        element.style.fontSize = `${candidate}px`;
        const lineHeight = renderedLineHeight(element, candidate);
        const renderedHeight = Math.max(
          element.scrollHeight,
          element.getBoundingClientRect().height,
        );
        const fitsWidth = element.scrollWidth <= width + 0.5;
        if (fitsWidth && renderedHeight <= lineHeight * maxLines + 0.5) {
          selected = candidate;
          fitsTarget = true;
          break;
        }
      }
      if (!fitsTarget) {
        element.style.fontSize = `${selected}px`;
        shouldBreakLongWords = element.scrollWidth > width + 0.5;
      }
    } else {
      selected = candidates[0];
    }

    applyFontSize(
      element,
      Math.min(selected, sharedFontSizeRef.current ?? selected),
    );
    element.style.overflowWrap = shouldBreakLongWords ? "break-word" : "normal";
    reportCandidate?.(memberId, group, selected);
    setCandidateFontSize(selected);
    setBreakLongWords(shouldBreakLongWords);
    setReady(true);
  }, [
    applyFontSize,
    enabled,
    group,
    maxLines,
    memberId,
    minimumSize,
    normalSize,
    reportCandidate,
    typography.size,
  ]);

  useLayoutEffect(() => {
    sharedFontSizeRef.current = sharedFontSize;
    const element = elementRef.current;
    if (!element || !enabled) return;
    applyFontSize(element, fontSize);
  }, [applyFontSize, enabled, fontSize, sharedFontSize]);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    if (!enabled) {
      element.style.removeProperty("font-size");
      element.style.removeProperty("overflow-wrap");
      element.style.removeProperty("--adaptive-text-font-size");
      setReady(true);
      return;
    }

    setReady(false);
    fit();

    let animationFrame = 0;
    const resize = (width: number) => {
      if (Math.abs(width - measuredWidth.current) > 0.5) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = window.requestAnimationFrame(fit);
      }
    };
    let observer: ResizeObserver | undefined;
    const onWindowResize = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(fit);
    };

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(([entry]) =>
        resize(entry.contentRect.width),
      );
      observer.observe(element);
    }
    window.addEventListener("resize", onWindowResize);

    const fonts = document.fonts;
    const onFontsLoaded = () => fit();
    void fonts?.ready.then(onFontsLoaded);
    fonts?.addEventListener?.("loadingdone", onFontsLoaded);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer?.disconnect();
      window.removeEventListener("resize", onWindowResize);
      fonts?.removeEventListener?.("loadingdone", onFontsLoaded);
    };
  }, [contentKey, enabled, fit, typography.fontFamily]);

  useLayoutEffect(() => {
    if (!enabled || !unregister) return;
    return () => unregister(memberId);
  }, [enabled, group, memberId, unregister]);

  const adaptiveStyle: SxProps = enabled
    ? {
        boxSizing: "border-box",
        lineHeight: typography.lineHeight.normal,
        overflowWrap: breakLongWords ? "break-word" : "normal",
        wordBreak: "normal",
        whiteSpace: "pre-wrap",
        visibility: ready ? "visible" : "hidden",
      }
    : {};

  return { adaptiveStyle, elementRef, enabled, fontSize, group };
};
